import React, { useCallback, useContext, useEffect, useState } from 'react';
import './Anonymisation.scss';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactSVG } from 'react-svg';
import check from '../../Assets/icons/check.svg';
import download from '../../Assets/icons/download.svg';
import contractAnimation from '../../Assets/lottie/loading.json';
import LottieAnimation from '../../Components/Animation/LottieAnimation';
import ApiContext from '../../Api/ApiContext';
import RestrictReadOnly from '../../Helpers/RestrictReadOnly';

interface AnonymisationProps {
    id: string;
    display: boolean;
    selection: string;
    showButton: boolean;
    setSelection: (selection: string) => void;
    setShowButton: (show: boolean) => void;
    setContractContent: (content: string) => void;
    t: (key: string, options?: any) => string; // Assuming t is a translation function
}

interface AnonymisationType {
    type?: string;
    occurrences?: number;
}

const Anonymisation: React.FC<AnonymisationProps> = ({ id, display, selection, showButton, setSelection, setShowButton, setContractContent, t }) => {
    const [anonymisations, setAnonymisations] = useState<AnonymisationType[] | null>(null);
    const [anonymisationCounts, setAnonymisationCounts] = useState<Record<string, number> | null>(null);
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    const [fetchingData, setFetchingData] = useState<boolean>(true);
    const { getAccessTokenSilently } = useAuth0();
    const [selectionCount, setSelectionCount] = useState<number>(0);

    const api = useContext(ApiContext);

    const updateData = useCallback(async () => {
        const { data } = await api.post(
          `/anonymisations`, 
          { documentId: id }
        );
    
        if (data?.content !== undefined) {
            if (data.content?.length > 0) {
                setAnonymisations(data.content);
            } else {
                setAnonymisations(null);
            }
        }
        if (data?.url) {
            setDownloadUrl(data.url);
        }
    
        return data;
    }, [id, api]);  

    useEffect(() => {

        let interval = null;

        const fetchAnonymisations = async () => {
            try {
              const data = await updateData();
              if(data.content?.length > 0) {
                clearInterval(interval);
                }
            } catch (error) {
              console.error(error);
            }
        };

        if(!id) return;

        updateData().then(() => {
            interval = setInterval(fetchAnonymisations, 5000);
            setFetchingData(false);
        });

        fetchAnonymisations();
      
        return () => {
          clearInterval(interval);
        };
    }, [getAccessTokenSilently, id, updateData, api]);

    useEffect(() => {
        if (anonymisations?.length > 0) {
          const counts = {
            "First Name": 0,
            "Last Name": 0,
            "Company Name": 0,
            "Street Name": 0,
            "Social Security Number": 0,
            "Company Number": 0,
            "VAT Number": 0,
            "IBAN Number": 0,
            "Phone Number": 0,
            "Email Address": 0,
            "Custom": 0,
          };
          
          if(anonymisations?.length > 0) {
            anonymisations.forEach(anonymisation => { 
                const key = anonymisation.type || "Personal Information";
                if (typeof anonymisation.occurrences === 'number') {
                    if(counts.hasOwnProperty(key)){
                        counts[key] += anonymisation.occurrences;
                    } else {
                        counts[key] = anonymisation.occurrences;
                    }
                }
            });
        }
      
          setAnonymisationCounts(counts);
        }
    }, [anonymisations]);

    useEffect(() => {
        if(selection !== '') {
            
            const contractContent = document.querySelector('.contractContent');
            const regex = new RegExp(selection, 'gi');
            const matches = contractContent.innerHTML.match(regex);
            if(matches !== null) {
                setSelectionCount(matches.length);
            }
        }
    }, [selection]);

    const AwaitingResults = () => {

        return <div className="nds anon">
          <div className="icon">
            <LottieAnimation animationData={contractAnimation} />
          </div>
          <h3>{t("analysis.nds_1")}</h3>
          <p>
            {t("analysis.nds_2")}
          </p>
        </div>
    }

    const [ generating, setGenerating ] = useState(false);

    if(fetchingData) return null;

    if(anonymisationCounts === null) return <div className={"anonWrapper " + (display ? "show" : "hide")}><div className="anonymisation"> <AwaitingResults /> </div></div>

    function addAnonimisation() {

        api.post('/add-anonymisation',
            { documentId: id, anonymisation: selection, count: selectionCount }
        ).then(res => {
                setSelection('');
                setShowButton(false);
                setContractContent(res.data.prompt);
                updateData();
        });
    }

    function fetchFile() {
        if(!generating) {
            setGenerating(true);
            api.post('/get-file',
                { documentId: id }
            ).then(res => {
                setDownloadUrl(res.data.outputFile);
                const link = document.createElement('a');
                link.href = res.data.outputFile;
                link.setAttribute('download', 'download');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                setGenerating(false);
            });
        }
    }

    const Custom = () => {

        if(!showButton) return null;

        return (
            <div className="custom" onClick={addAnonimisation}>{t('anon.anonymise')} <span className="selection">{selection}</span> ({selectionCount})</div>
        )
    }

    const count = Object.values(anonymisationCounts).reduce(
        (a, b) => (isNaN(b) ? a : a + b),
        0
    );
    
    return (
        <div className={"anonWrapper " + (display ? "show" : "hide")}>
            <div className="anonymisation">
            <h3>{t('anon.completed', { count })}</h3>
            <div className="anonymisationList">
                <div className="anonymisationHeader">
                    <div className="type">Type</div>
                    <div className="count">#</div>
                    <div className="status">Status</div>
                </div>
                {anonymisationCounts && Object.entries(anonymisationCounts).map(([type, count], index) => (
                <div className={"anonymisationItem " + (count === 0 ? "none" : "")} key={index}>
                    <div className="type">{t(type)}</div>
                    <div className="count">{count}</div>
                    <div className="status"><ReactSVG src={check} /></div>
                </div>
                ))}
            </div>
            </div>
            <div className="footer">
                <Custom />
                <RestrictReadOnly>
                    {downloadUrl ?
                        <a className="download" href={downloadUrl} data-click-target="download" data-click-source="anonymisation"  download><ReactSVG src={download} />{t("anon.download")}</a> :
                        <div className="download" onClick={fetchFile} data-click-target="download" data-click-source="anonymisation"><ReactSVG src={download} />{generating ? t("anon.generating") : t("anon.download")}</div>
                    }
                </RestrictReadOnly>
            </div>
        </div>
      );
}

export default Anonymisation;